import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ChatIcon from '../../../icons/ChatIcon';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import useParticipantWindowContext from '../../../hooks/useParticipantWindowContext/useParticipantWindowContext';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';

export const ANIMATION_DURATION = 700;

const expand = keyframes`
  0% {
    transform: scale(0.1, 0.1);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1.4, 1.4);
    opacity: 0;
  }
`;

const IconContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'activeChat',
})<{ activeChat?: boolean }>(({ activeChat }) => ({
  position: 'relative',
  display: 'flex',
  ...(activeChat && {
    '& svg': {
      filter: 'invert(27%) sepia(11%) saturate(2879%) hue-rotate(80deg) brightness(98%) contrast(100%)',
    },
  }),
}));

const Circle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasUnreadMessages',
})<{ hasUnreadMessages?: boolean }>(({ hasUnreadMessages }) => ({
  width: '10px',
  height: '10px',
  backgroundColor: '#5BB75B',
  borderRadius: '50%',
  position: 'absolute',
  top: '-3px',
  left: '13px',
  opacity: 0,
  transition: `opacity ${ANIMATION_DURATION * 0.5}ms ease-in`,
  ...(hasUnreadMessages && {
    opacity: 1,
  }),
}));

const Ring = styled('div', {
  shouldForwardProp: (prop) => prop !== 'shouldAnimate',
})<{ shouldAnimate?: boolean }>(({ shouldAnimate }) => ({
  border: '3px solid #5BB75B',
  borderRadius: '30px',
  height: '14px',
  width: '14px',
  position: 'absolute',
  left: '11px',
  top: '-5px',
  opacity: 0,
  ...(shouldAnimate && {
    animation: `${expand} ${ANIMATION_DURATION}ms ease-out`,
    animationIterationCount: 1,
  }),
}));

export default function ToggleChatButton() {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { isChatWindowOpen, setIsChatWindowOpen, conversation, hasUnreadMessages } = useChatContext();
  const { setIsBackgroundSelectionOpen } = useVideoContext();
  const { setIsParticipantWindowOpen } = useParticipantWindowContext();

  const toggleChatWindow = () => {
    setIsChatWindowOpen(!isChatWindowOpen);
    setIsParticipantWindowOpen(false);
    setIsBackgroundSelectionOpen(false);
  };

  useEffect(() => {
    if (shouldAnimate) {
      setTimeout(() => setShouldAnimate(false), ANIMATION_DURATION);
    }
  }, [shouldAnimate]);

  useEffect(() => {
    if (conversation && !isChatWindowOpen) {
      const handleNewMessage = () => setShouldAnimate(true);
      conversation.on('messageAdded', handleNewMessage);
      return () => {
        conversation.off('messageAdded', handleNewMessage);
      };
    }
  }, [conversation, isChatWindowOpen]);

  return (
    <Tooltip
      title="Chat"
      placement="top"
      PopperProps={{ disablePortal: true }}
      style={{ cursor: !conversation ? 'not-allowed' : 'pointer' }}
    >
      <span>
        <Button
          data-cy-chat-button
          onClick={toggleChatWindow}
          disabled={!conversation}
          startIcon={
            <IconContainer activeChat={isChatWindowOpen}>
              <ChatIcon />
              <Ring shouldAnimate={shouldAnimate} />
              <Circle hasUnreadMessages={hasUnreadMessages} />
            </IconContainer>
          }
        >
          {/* Chat */}
        </Button>

      </span>
    </Tooltip>
  );
}
